import React, { useEffect, useState, useCallback } from 'react';
import {
  Users as UsersIcon,
  UserPlus,
  Trash2,
  ShieldCheck,
  ShieldHalf,
  Eye,
  Loader,
  AlertTriangle,
  X,
  Mail,
  Lock,
  User as UserIcon,
} from 'lucide-react';
import PageHeader from '../common/PageHeader';
import ConfirmModal from '../common/ConfirmModal';
import { EmptyState, PrimaryCTA, GlowIconTile } from '../common/Shinobi';
import { adminUsersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatAbsoluteDate } from '../../utils/time';

const ROLE_META = {
  pentester: {
    label: 'Red Teamer',
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.15)',
    icon: ShieldCheck,
    description: 'Full access — offensive engagements, monitoring, beacons, and user management.',
  },
  blue_team: {
    label: 'Blue Teamer',
    color: '#3b82f6',
    bg: 'rgba(59, 130, 246, 0.15)',
    icon: ShieldHalf,
    description: 'Monitoring plane only — subdomain monitoring, OSINT leaks, asset inventory, vulnerability findings, and monitor beacons. No offensive access.',
  },
  wasabian: {
    label: 'Wasabian',
    color: '#94A3B8',
    bg: 'rgba(148, 163, 184, 0.15)',
    icon: Eye,
    description: 'Read-only viewer — dashboards, reports, and inventory.',
  },
};

const RoleChip = ({ role }) => {
  const meta = ROLE_META[role] || ROLE_META.wasabian;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border"
      style={{ color: meta.color, backgroundColor: meta.bg, borderColor: meta.color }}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return formatAbsoluteDate(iso);
};

const AddUserModal = ({ isOpen, onClose, onCreated }) => {
  const [form, setForm] = useState({
    email: '',
    username: '',
    full_name: '',
    password: '',
    role: 'wasabian',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setForm({ email: '', username: '', full_name: '', password: '', role: 'wasabian' });
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        email: form.email.trim(),
        username: form.username.trim(),
        password: form.password,
        role: form.role,
      };
      if (form.full_name.trim()) payload.full_name = form.full_name.trim();
      const response = await adminUsersAPI.create(payload);
      onCreated(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      />
      <div
        className="relative rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.15)',
        }}
      >
        <div
          className="relative px-7 pt-6 pb-5 overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, rgba(72, 185, 76, 0.10) 0%, transparent 100%)',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div
            className="absolute -top-12 -right-12 w-48 h-48 rounded-full blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(0, 206, 62, 0.25), transparent 70%)' }}
          />
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-4">
              <GlowIconTile icon={UserPlus} accent="green" />
              <div>
                <h2 className="text-xl font-bold tracking-tight text-slate-900">Add User</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Create a Pentester or Wasabian and share credentials out-of-band.
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900 p-1 -m-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="px-7 py-6 space-y-5">
          {error && (
            <div className="rounded-lg border border-danger-red/40 bg-danger-red/5 p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-danger-red flex-shrink-0 mt-0.5" />
              <p className="text-danger-red text-sm">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
              Email <span className="text-shinobi-green">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-shinobi-green" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="user@wasabi.com"
                className="terminal-input w-full pl-10 pr-4 py-2.5 rounded-lg text-sm"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
              Username <span className="text-shinobi-green">*</span>
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-shinobi-green" />
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="lowercase, no spaces"
                pattern="[a-zA-Z0-9_.\-]+"
                minLength={3}
                maxLength={64}
                className="terminal-input w-full pl-10 pr-4 py-2.5 rounded-lg text-sm"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
              Full Name <span className="text-gray-600 font-normal normal-case">optional</span>
            </label>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Jane Operative"
              className="terminal-input w-full px-4 py-2.5 rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
              Password <span className="text-shinobi-green">*</span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-shinobi-green" />
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="min 8 chars; share with the new user"
                minLength={8}
                className="terminal-input w-full pl-10 pr-4 py-2.5 rounded-lg text-sm font-mono"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
              Role
            </label>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(ROLE_META).map(([id, meta]) => {
                const active = form.role === id;
                const Icon = meta.icon;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setForm({ ...form, role: id })}
                    className="text-left p-3 rounded-lg transition-all"
                    style={
                      active
                        ? {
                            background: meta.bg,
                            border: `1px solid ${meta.color}`,
                            boxShadow: 'none',
                          }
                        : {
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                          }
                    }
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: active ? meta.color : '#94A3B8' }} />
                      <p
                        className="text-sm font-bold"
                        style={{ color: active ? meta.color : '#475569' }}
                      >
                        {meta.label}
                      </p>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{meta.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-sm text-slate-500 hover:text-slate-900 transition-colors"
            >
              Cancel
            </button>
            <PrimaryCTA type="submit" loading={submitting} variant="green">
              Create User
            </PrimaryCTA>
          </div>
        </form>
      </div>
    </div>
  );
};

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, user: null });
  const [busyUserId, setBusyUserId] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminUsersAPI.list();
      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleChange = async (u, nextRole) => {
    if (!nextRole || nextRole === u.role) return;
    setBusyUserId(u.id);
    try {
      await adminUsersAPI.update(u.id, { role: nextRole });
      await fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleActiveToggle = async (u) => {
    setBusyUserId(u.id);
    try {
      await adminUsersAPI.update(u.id, { is_active: !u.is_active });
      await fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDelete = async () => {
    const target = confirmDelete.user;
    if (!target) return;
    setBusyUserId(target.id);
    try {
      await adminUsersAPI.delete(target.id);
      setConfirmDelete({ isOpen: false, user: null });
      await fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="h-full overflow-auto">
      <PageHeader
        title="Users"
        subtitle="Internal Wasabi Red Team — Pentesters and Wasabians"
        icon={UsersIcon}
        actions={
          <PrimaryCTA onClick={() => setShowAdd(true)} icon={UserPlus} iconPosition="left" variant="green">
            Add User
          </PrimaryCTA>
        }
      />

      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader className="w-5 h-5 animate-spin text-shinobi-green mr-2" />
            <span>Loading users…</span>
          </div>
        ) : error ? (
          <div className="rounded-lg border border-danger-red/40 bg-danger-red/5 p-4 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-danger-red flex-shrink-0 mt-0.5" />
            <p className="text-danger-red text-sm">{error}</p>
          </div>
        ) : users.length === 0 ? (
          <div className="cyber-card rounded-xl">
            <EmptyState
              icon={UsersIcon}
              title="No users yet"
              description="Create your first Pentester or Wasabian. Pentesters can run all tools; Wasabians can view dashboards and reports."
              actionLabel="Add User"
              actionIcon={UserPlus}
              onAction={() => setShowAdd(true)}
              accent="green"
            />
          </div>
        ) : (
          <div className="cyber-card rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="text-left font-semibold px-5 py-3">User</th>
                  <th className="text-left font-semibold px-5 py-3">Role</th>
                  <th className="text-left font-semibold px-5 py-3">Status</th>
                  <th className="text-left font-semibold px-5 py-3">Last Login</th>
                  <th className="text-left font-semibold px-5 py-3">Created</th>
                  <th className="text-right font-semibold px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = currentUser?.id === u.id;
                  const busy = busyUserId === u.id;
                  return (
                    <tr key={u.id} className="border-b border-slate-200/60 hover:bg-cyber-200/40 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                            style={{
                              background: '#48b94c',
                              color: '#ffffff',
                            }}
                          >
                            {u.username?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate">
                              {u.full_name || u.username}
                              {isSelf && <span className="text-[10px] text-gray-500 ml-2">(you)</span>}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <RoleChip role={u.role} />
                      </td>
                      <td className="px-5 py-3">
                        {u.is_active ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-shinobi-green">
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ background: '#48b94c' }}
                            />
                            Active
                          </span>
                        ) : (
                          <span className="text-xs font-semibold text-gray-500">Disabled</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{formatDate(u.last_login)}</td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{formatDate(u.created_at)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <select
                            value={u.role}
                            onChange={(e) => handleRoleChange(u, e.target.value)}
                            disabled={busy || isSelf}
                            className="text-[11px] font-semibold px-2 py-1 rounded-md border border-slate-200 bg-cyber-300 text-slate-700 hover:border-wasabi-300 focus:outline-none focus:border-shinobi-green/60 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title={isSelf ? 'cannot change your own role' : 'change role'}
                          >
                            {Object.entries(ROLE_META).map(([id, meta]) => (
                              <option key={id} value={id}>{meta.label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleActiveToggle(u)}
                            disabled={busy || isSelf}
                            className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-slate-200 text-slate-700 hover:border-cyber-yellow/40 hover:text-cyber-yellow disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title={isSelf ? 'cannot deactivate yourself' : u.is_active ? 'deactivate' : 'activate'}
                          >
                            {u.is_active ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            onClick={() => setConfirmDelete({ isOpen: true, user: u })}
                            disabled={busy || isSelf}
                            className="p-1.5 rounded-md text-gray-500 hover:text-danger-red hover:bg-danger-red/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title={isSelf ? 'cannot delete yourself' : 'delete user'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddUserModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={() => fetchUsers()}
      />

      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, user: null })}
        onConfirm={handleDelete}
        title="Delete user"
        message={
          confirmDelete.user
            ? `Are you sure you want to permanently delete ${confirmDelete.user.email}? This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default UsersPage;
